<script setup lang="ts">
import { computed, provide } from "vue";
import { useRoute } from "vue-router";
import Spinner from "@/components/helpers/Spinner.vue";
import MyStudents403Forbidden from "@/components/MyStudentDetail/MyStudents403Forbidden.vue";
import { useMyStudentsNavigation } from "@/components/MyStudents/composables/useMyStudentsNavigation.js";
import TopMenu from "@/components/MyStudents/Navigation/TopMenu.vue";
import InternalComments from "@/components/MyStudents/Submissions/components/InternalComments/index.vue";
import RecommenderResponses from "@/components/MyStudents/Submissions/components/RecommenderResponses/index.vue";
import SubmissionErrorState from "@/components/MyStudents/Submissions/components/SubmissionErrorState.vue";
import SubmissionsHeader from "@/components/MyStudents/Submissions/components/SubmissionsHeader/index.vue";
import SubmissionsInformation from "@/components/MyStudents/Submissions/components/SubmissionsInformation/index.vue";
import type { SubmissionReadOverride } from "@/components/MyStudents/Submissions/components/SubmissionsInformation/types";
import { isLorTodo } from "@/components/MyStudents/Submissions/letterOfRecommendation";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import { hasTodoViewSubmissionPermission } from "@/composables/authorization.js";
import { useSubmissionReviewData } from "./composables/useSubmissionReviewData";
import { TODO_SUBMISSION_STATUS_KEY, useTodoSubmissionStatus } from "./composables/useTodoSubmissionStatus";

const route = useRoute();
const {
  isLoading,
  applicationId,
  todoId,
  participantId,
  participantName,
  participantEmail,
  programData,
  sessionData,
  todoSubmissionData,
  isTodoSubmissionError,
} = useSubmissionReviewData();
const { sections, title, toastMenu } = useMyStudentsNavigation();

const recentSubmission = computed<SubmissionReadOverride | undefined>(() => {
  const sorted = [...(todoSubmissionData.value?.submissions ?? [])].sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return dateB - dateA;
  });
  return sorted[0] as SubmissionReadOverride | undefined;
});

const todoSubmissionStatus = useTodoSubmissionStatus({
  todoSubmission: todoSubmissionData,
  recentSubmission,
  applicationId,
  todoId,
});
provide(TODO_SUBMISSION_STATUS_KEY, todoSubmissionStatus);

const todoTitle = computed(() => {
  if (isTodoSubmissionError.value) {
    return "Submission";
  }
  return todoSubmissionData.value?.title ?? "Submission";
});
const todoDueDate = computed(() => todoSubmissionData.value?.due_date);
const isLorSubmission = computed<boolean>(() => isLorTodo(todoId.value));
const todoSubmissions = computed<SubmissionReadOverride[]>(
  () => (todoSubmissionData.value?.submissions ?? []) as SubmissionReadOverride[],
);

const studentDetailTo = computed(() => ({
  name: "student-management",
  query: route.query,
  params: { page: "student-detail", oktaId: participantId.value },
}));
</script>

<template>
  <MyStudents403Forbidden v-if="!hasTodoViewSubmissionPermission" />
  <Spinner v-else-if="isLoading" />
  <div v-else class="bg-blue-100">
    <TopMenu
      :sections="sections"
      :title="title"
      :show-popup-menu="true"
      :toast-menu="toastMenu"
      data-test-id="top-menu"
      class="!px-5"
    />
    <div class="p-5">
      <div class="font-montserrat text-13p text-indigo-base flex mb-6">
        <router-link
          class="hover:underline"
          :to="{
            name: 'student-management',
            query: route.query,
            params: { page: 'listing' },
          }"
          >My Students</router-link
        >
        <ArrowRight custom-class="w-2 mr-1 ml-1 text-gray-500" />
        <router-link class="hover:underline" :to="studentDetailTo">{{ participantName }}</router-link>
        <ArrowRight custom-class="w-2 mr-1 ml-1 text-gray-500" />
        <span class="text-gray-600">{{ todoTitle }}</span>
      </div>
      <SubmissionErrorState v-if="isTodoSubmissionError" :student-detail-to="studentDetailTo" />
      <template v-else>
        <SubmissionsHeader
          class="mb-2"
          :participant-name="participantName"
          :todo-title="todoTitle"
          :program-data="programData"
          :session-data="sessionData"
          :due-date="todoDueDate"
        />
        <RecommenderResponses
          v-if="isLorSubmission"
          class="mt-6"
          :application-id="applicationId"
          :todo-id="todoId"
          :todo-submissions="todoSubmissions"
        />
        <SubmissionsInformation
          :todo-submission="todoSubmissionData"
          :participant-email="participantEmail"
        />
        <InternalComments
          :application-id="applicationId"
          :todo-id="todoId"
        />
      </template>
    </div>
  </div>
</template>
